import { SPECIAL_REQUEST_CATEGORIES } from '../constants/appConstants';
import { resolveShapeOptions } from './shapeOptions';

export const SHAPE_OTHER = 'OTHER';

export const DEFAULT_SPECIAL_REQUEST_FORM = {
  category: SPECIAL_REQUEST_CATEGORIES.CERTIFIED,
  description: '',
  details: {
    shape: '',
    shapeOther: '',
    color: '',
    clarity: '',
    carat: '',
    length: '',
    width: '',
    comment: '',
  },
};

export function buildShapeSelectOptions(shapeList, t) {
  const shapes = resolveShapeOptions(shapeList);
  return [
    ...shapes.map((s) => ({ value: s, label: s })),
    { value: SHAPE_OTHER, label: t('specialRequests.shapeOther') },
  ];
}

export function resolveRequestShapeLabel(details) {
  if (!details) return '';
  if (details.shape === SHAPE_OTHER) return details.shapeOther || 'Other';
  return details.shape || details.shapeOther || '';
}

export function buildSpecialRequestPayload(form, { clientId } = {}) {
  const category = form.category;
  const d = form.details || {};
  const details = {
    shape: d.shape || '',
    shapeOther: d.shape === SHAPE_OTHER ? (d.shapeOther || '').trim() : '',
    comment: (d.comment || '').trim(),
  };

  if (category === SPECIAL_REQUEST_CATEGORIES.CERTIFIED) {
    details.color = (d.color || '').trim();
    details.clarity = (d.clarity || '').trim();
    if (d.carat !== '' && d.carat != null) details.carat = Number(d.carat);
  } else if (category === SPECIAL_REQUEST_CATEGORIES.NON_CERTIFIED) {
    if (d.length !== '' && d.length != null) details.length = Number(d.length);
    if (d.width !== '' && d.width != null) details.width = Number(d.width);
  }

  const payload = { category, details };

  if (category === SPECIAL_REQUEST_CATEGORIES.OTHER) {
    payload.description = (form.description || details.comment || '').trim();
  }

  if (clientId) {
    payload.clientId = clientId;
  }

  return payload;
}

export function validateSpecialRequestForm(form, t) {
  const category = form.category;
  const d = form.details || {};

  if (category === SPECIAL_REQUEST_CATEGORIES.CERTIFIED) {
    if (!d.shape) return t('specialRequests.validation.shapeRequired');
    if (d.shape === SHAPE_OTHER && !d.shapeOther?.trim()) return t('specialRequests.validation.shapeOtherRequired');
    if (d.carat === '' || d.carat == null || Number(d.carat) <= 0) return t('specialRequests.validation.caratRequired');
  } else if (category === SPECIAL_REQUEST_CATEGORIES.NON_CERTIFIED) {
    if (!d.shape) return t('specialRequests.validation.shapeRequired');
    if (d.shape === SHAPE_OTHER && !d.shapeOther?.trim()) return t('specialRequests.validation.shapeOtherRequired');
    const hasLen = d.length !== '' && d.length != null && Number(d.length) > 0;
    const hasWid = d.width !== '' && d.width != null && Number(d.width) > 0;
    if (!hasLen && !hasWid) return t('specialRequests.validation.measurementsRequired');
  } else {
    const text = (form.description || d.comment || '').trim();
    if (text.length < 10) return t('specialRequests.descriptionMinLength');
  }

  return null;
}

export function buildOrderDescriptionFromRequest(request) {
  if (!request) return '';
  const category = request.category;
  const d = request.details || {};
  const parts = [];

  if (category === SPECIAL_REQUEST_CATEGORIES.CERTIFIED) {
    const shape = resolveRequestShapeLabel(d);
    if (shape) parts.push(shape);
    if (d.carat != null) parts.push(`${d.carat}ct`);
    if (d.color) parts.push(d.color);
    if (d.clarity) parts.push(d.clarity);
  } else if (category === SPECIAL_REQUEST_CATEGORIES.NON_CERTIFIED) {
    const shape = resolveRequestShapeLabel(d);
    if (shape) parts.push(shape);
    const dims = [];
    if (d.length != null) dims.push(`${d.length}mm L`);
    if (d.width != null) dims.push(`${d.width}mm W`);
    if (dims.length) parts.push(dims.join(' × '));
  } else {
    return request.description || d.comment || '';
  }

  let summary = parts.join(' ');
  if (d.comment) {
    summary = summary ? `${summary} — ${d.comment}` : d.comment;
  }
  return summary.trim() || request.description || '';
}

export function formatSpecialRequestOrderRef(order) {
  const id = order?._id || order;
  if (!id) return '';
  return String(id).slice(-6).toUpperCase();
}

export function getSpecialRequestOrderId(request) {
  return request?.convertedOrder?._id || request?.convertedOrder || null;
}

export function mapRequestToForm(request) {
  if (!request) return { ...DEFAULT_SPECIAL_REQUEST_FORM };
  const d = request.details || {};
  return {
    category: request.category || SPECIAL_REQUEST_CATEGORIES.CERTIFIED,
    description: request.description || '',
    details: {
      shape: d.shape || '',
      shapeOther: d.shapeOther || '',
      color: d.color || '',
      clarity: d.clarity || '',
      carat: d.carat != null ? String(d.carat) : '',
      length: d.length != null ? String(d.length) : '',
      width: d.width != null ? String(d.width) : '',
      comment: d.comment || '',
    },
  };
}
